// ─────────────────────────────────────────────────────────────────────────────
// Blocks and fields that only apply to some answers
//
// A sub-section — or a single field — may state when it does NOT apply:
//
//   { "sectionType": "subSection", "sectionName": "…",
//     "hideWhen": { "field": "mode_of_commercialisation", "in": ["Self Manufacture"] } }
//
//   { "name": "tk_other_source", "label": "…",
//     "hideWhen": { "field": "tk_holder_type", "notIn": ["Other Source"] } }
//
// `in` / `equals` hide it FOR those answers; `notIn` hides it for every answer
// but those (an unanswered question counts as "not those", so the exceptional
// branch stays shut until it is actually chosen).
//
// A hidden block is not rendered, not validated, not summarised, not shown in
// the preview, and its answers are dropped from the row when it is saved —
// every walker asks isBlockHidden() rather than re-deriving the rule.
//
// FORM_9 (3)(i): "(d) Details of each licensee / assignee / transferee / any
// other person" names the OTHER party to a commercialisation. With the mode
// "Self Manufacture" there is no other party, yet the block's required Title /
// First Name / Address fields still blocked the entry from being saved.
// withBlockRules() gives that block its hideWhen when the template does not,
// keyed to the sibling "Mode of commercialisation" field found by label, so the
// rule holds whatever the backend calls the field. An explicit hideWhen in the
// template always wins.
// ─────────────────────────────────────────────────────────────────────────────

import { splitLabelMarker, labelText, markerStyle, formatMarker } from './labelNumbering';

const norm = v => String(v ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const OTHER_PARTY_BLOCK = /licensee|assignee|transferee/i;
const MODE_FIELD = /mode\s+of\s+commerciali[sz]ation/i;
const SELF_MODES = ['Self Manufacture', 'Self Manufacturing'];

/* FORM_7 (6) "Details of any traditional knowledge associated thereto
   accessed" asks WHO holds the knowledge — "(ii) Name of the holder(s) …
   (individual / community)" — with three answers: Individual, Community, and
   Other Source. The two that name a holder are followed by that holder's
   address; the third is followed instead by a description of the other source.

   The template shows both at once, and both are required, so an applicant who
   answered "Other Source" was still made to fill in the village, town,
   district and state of a holder they had just said they do not have. The two
   branches exclude each other, so each is shown for its own answers only. */
const TK_HOLDER_FIELD   = /holder.*traditional\s+knowledge|traditional\s+knowledge.*holder/i;
const TK_ADDRESS_BLOCK  = /address\s+of\s+the\s+traditional\s+knowledge\s+holder/i;
const TK_OTHER_SOURCE   = /obtained\s+from\s+any\s+other\s+source/i;
const OTHER_SOURCE_ANSWER = 'Other Source';

// Keys an upload / number-unit field stores beside its own value.
const COMPANION_SUFFIXES = ['_proofType', '_proof_type', '_idNumber', '_id_number', '_unit'];

/**
 * Whether a block's (or a field's) hideWhen rule is met by the current answers.
 *
 * @param {object}   def  a field or sub-section definition
 * @param {Function} get  field name → current value
 */
export function isBlockHidden(def, get) {
  const rule = def?.hideWhen;
  if (!rule?.field || typeof get !== 'function') return false;
  const current = norm(get(rule.field));
  /* "Only for these answers". An unanswered question is not one of them, so
     the block stays hidden until the answer that opens it is chosen. */
  if (Array.isArray(rule.notIn)) return !rule.notIn.some(v => norm(v) === current);
  if (!current) return false;
  const values = Array.isArray(rule.in) ? rule.in : (rule.equals !== undefined ? [rule.equals] : []);
  return values.some(v => norm(v) === current);
}

const hayFor = f => `${f?.label || ''} ${String(f?.name || '').replace(/_/g, ' ')}`;

const isModeField = f => !!(f && f.name && MODE_FIELD.test(hayFor(f)));

/* The "who holds it" question: a choice whose answers include "Other Source". */
const isTkHolderField = f => !!(f && f.name && Array.isArray(f.options) &&
  TK_HOLDER_FIELD.test(hayFor(f)) &&
  f.options.some(o => norm(typeof o === 'object' ? (o?.value ?? o?.label) : o) === norm(OTHER_SOURCE_ANSWER)));

/**
 * The same definitions with the built-in block rules attached. Returns the
 * input array itself when nothing changed, so memoised consumers keep identity.
 */
export function withBlockRules(defs) {
  if (!Array.isArray(defs)) return defs;
  const mode = defs.find(isModeField);
  const tkHolder = defs.find(isTkHolderField);
  let changed = false;
  const out = defs.map(f => {
    if (!f || typeof f !== 'object') return f;
    let next = f;
    if (Array.isArray(f.fields)) {
      const kids = withBlockRules(f.fields);
      if (kids !== f.fields) next = { ...next, fields: kids };
    }
    if (f.conditions && typeof f.conditions === 'object') {
      let condChanged = false;
      const conditions = Object.fromEntries(Object.entries(f.conditions).map(([k, br]) => {
        const nb = withBlockRules(br);
        if (nb !== br) condChanged = true;
        return [k, nb];
      }));
      if (condChanged) next = { ...next, conditions };
    }
    if (mode && f.sectionType === 'subSection' && f.type !== 'repeatable' && !f.hideWhen &&
        OTHER_PARTY_BLOCK.test(f.sectionName || f.label || '')) {
      next = { ...next, hideWhen: { field: mode.name, in: SELF_MODES } };
    }
    /* The holder's address applies to a named holder; the other-source
       description applies instead of one. Never both. */
    if (tkHolder && f !== tkHolder && !f.hideWhen && f.type !== 'repeatable') {
      const heading = f.sectionName || f.label || '';
      if (f.sectionType === 'subSection' && TK_ADDRESS_BLOCK.test(heading)) {
        next = { ...next, hideWhen: { field: tkHolder.name, in: [OTHER_SOURCE_ANSWER] } };
      } else if (f.name && TK_OTHER_SOURCE.test(heading)) {
        next = { ...next, hideWhen: { field: tkHolder.name, notIn: [OTHER_SOURCE_ANSWER] } };
      }
    }
    if (next !== f) changed = true;
    return next;
  });
  return changed ? out : defs;
}

/** Every value key a block's fields can write, companions included. */
export const blockValueKeys = (defs, out = new Set()) => {
  (defs || []).forEach(f => {
    if (!f) return;
    if (Array.isArray(f.fields)) blockValueKeys(f.fields, out);
    if (f.conditions) Object.values(f.conditions).forEach(br => blockValueKeys(br, out));
    if (!f.name) return;
    out.add(f.name);
    COMPANION_SUFFIXES.forEach(s => out.add(`${f.name}${s}`));
    if (f.othersConditionalField?.name) out.add(f.othersConditionalField.name);
  });
  return out;
};

/**
 * The row with every answer inside a hidden block cleared, so an entry saved as
 * "Self Manufacture" does not carry a licensee typed in before the mode changed.
 * Returns the row itself when no block is hidden.
 */
export function stripHiddenBlocks(defs, row) {
  if (!row || typeof row !== 'object') return row;
  const drop = new Set();
  const walk = list => (list || []).forEach(f => {
    if (!f) return;
    if (isBlockHidden(f, n => row[n])) {
      // A hidden single field drops its own answer; a hidden block, all of its.
      blockValueKeys(f.fields || [f], drop);
      return;
    }
    if (Array.isArray(f.fields)) walk(f.fields);
    if (f.conditions) Object.values(f.conditions).forEach(walk);
  });
  walk(defs);
  const keys = [...drop].filter(k => k in row && row[k] !== '');
  if (!keys.length) return row;
  const next = { ...row };
  keys.forEach(k => { next[k] = ''; });
  return next;
}

/* ── Yes / No toggles ──────────────────────────────────────────────────────
   Which answer of a toggle opens its block. Templates have spelled this three
   ways — `triggerValue` (FORM_9's "(v) Whether the details of the biological
   resources … is one and the same?" opens its resource list on "No"),
   `showFieldsOnValue` and `fieldsVisibleWhen` — while the renderer read one of
   the latter two in some places and assumed "Yes" in the rest. A block that
   opened on "No" therefore never appeared, was never validated and its rows
   were never saved. Every toggle check asks this instead. */
export const toggleOpenValue = toggleField =>
  toggleField?.triggerValue ?? toggleField?.showFieldsOnValue ?? toggleField?.fieldsVisibleWhen ?? 'Yes';

/* ── A section whose HEADING already asks the question ─────────────────────
   FORM_10 (6) is headed "(6) Whether approval of the Competent National
   Authority of the country of origin of biological resource has been
   obtained" — and then asks the very same thing again as the section's first
   field, "Whether approval of the Competent National Authority has been
   obtained *", with Yes / No radios. Two problems for the applicant: the same
   sentence is read twice, and the "Details of the approval" block underneath
   stayed open (and required) even after answering No.

   A leading Yes/No field that only restates its own heading IS that section's
   gate, so it is turned into one: `toggleField`, answered once in the section
   header, with the rest of the section shown only for the answer that opens
   it. The answer is still stored under the field's own name — nothing is
   dropped, it is just asked once. A template that declares its own
   `toggleField` is left exactly as it is. */

const optionValue = o => (o && typeof o === 'object') ? (o.value ?? o.label ?? '') : o;

/** The field's two options when they are exactly Yes and No, else null. */
const yesNoOptions = f => {
  const opts = (f?.options || []).map(optionValue).filter(o => String(o ?? '').trim() !== '');
  if (opts.length !== 2) return null;
  const kinds = opts.map(norm);
  return kinds.includes('yes') && kinds.includes('no') ? opts : null;
};

/* Grammar and boilerplate carry no meaning here — "whether approval … has been
   obtained" and "whether approval … of the country of origin … has been
   obtained" are the same question stated at two lengths. */
const FILLER = new Set(['a','an','the','of','in','on','at','to','for','from','by','with','as',
  'is','are','was','were','be','been','being','has','have','had','its','their','this','that',
  'any','all','and','or','if','under','whether','obtained','details','detail','please','select']);

const contentTokens = text =>
  norm(labelText(text)).split(' ').filter(t => t && !FILLER.has(t));

/** Does `label` say nothing the section heading does not already say? */
const restatesHeading = (label, heading) => {
  const want = contentTokens(label);
  if (want.length < 3) return false;          // too short to be sure it is the same question
  const have = new Set(contentTokens(heading));
  return want.every(t => have.has(t));
};

/**
 * The section with its leading restated Yes/No question lifted into a
 * `toggleField`. Returns the section itself when there is nothing to lift.
 */
export function hoistSectionToggle(sec) {
  if (!sec || typeof sec !== 'object' || sec.toggleField) return sec;
  if (!sec.sectionName || !Array.isArray(sec.fields) || !sec.fields.length) return sec;
  // Declarations and the BMC picker are rendered whole by their own components.
  if (sec.sectionType || /declaration/i.test(sec.sectionName)) return sec;
  const lead = sec.fields[0];
  if (!lead?.name || (lead.type !== 'radio' && lead.type !== 'select')) return sec;
  const opts = yesNoOptions(lead);
  if (!opts || !restatesHeading(lead.label, sec.sectionName)) return sec;
  /* What the gate opens onto. A single plain block under it carries a heading
     that is the gate's own heading said a third time ("… approval … has been
     obtained" → "Details of the approval"), so the block's fields — which have
     their own labels — stand directly under the section instead. */
  let body = sec.fields.slice(1);
  if (body.length === 1 && isPlainBlock(body[0])) body = body[0].fields;
  return {
    ...sec,
    toggleField: {
      name: lead.name,
      options: opts,
      /* No pre-selected answer: the question is required, and a toggle that
         shows "No" before it has been touched answers it for the applicant. */
      default: '',
      triggerValue: opts.find(o => norm(o) === 'yes') ?? 'Yes',
      required: !!lead.required,
      requiredLabel: lead.label || sec.sectionName,
    },
    fields: body,
  };
}

/* ── A gate on the block that is not the question ─────────────────────────
   FORM_10 (5)(iii) is headed "If the internationally recognized certificate of
   compliance is not available, the following information may be provided" —
   that IS the Yes/No question, and everything under it is what the answer
   opens. The template instead hung the toggle on the block inside it, "(a)
   Source of the biological resource", which is a heading, not a question: the
   applicant was asked "Source of the biological resource — Yes / No?" and the
   source fields stayed shut behind it.

   So the gate moves up to the block whose heading actually asks: the outer
   block carries the toggle, and the inner one goes back to being a heading
   with its fields under it. The answer keeps its own field name, so nothing
   already saved changes meaning. Only a single un-asked gate inside a
   question is moved — a block that asks its own question keeps its toggle. */

/* "If …", "Whether …", "Has … been obtained", "… one and the same?" */
const QUESTION_START = /^(if|whether|do|does|did|is|are|was|were|has|have|had|can|could|shall|should|will|would)\b/i;
const asksAQuestion = heading => {
  const t = labelText(heading).trim();
  return !!t && (/\?\s*$/.test(t) || QUESTION_START.test(t));
};

/** A sub-section that is just a heading with fields — no gate, no rule of its own. */
const isPlainBlock = f => !!f && f.sectionType === 'subSection' && f.type !== 'repeatable' &&
  !f.toggleField && !f.hideWhen && Array.isArray(f.fields) && f.fields.length > 0;

function liftToggleToQuestion(block) {
  if (!block || block.sectionType !== 'subSection' || block.type === 'repeatable') return block;
  if (block.toggleField || !Array.isArray(block.fields)) return block;
  if (!asksAQuestion(block.sectionName)) return block;
  const gated = block.fields.filter(f =>
    f && f.sectionType === 'subSection' && f.type !== 'repeatable' && f.toggleField?.name);
  if (gated.length !== 1) return block;               // two gates: the template means them
  const [child] = gated;
  if (asksAQuestion(child.sectionName)) return block; // the child asks its own question
  const { toggleField, ...heading } = child;
  return { ...block, toggleField, fields: block.fields.map(f => (f === child ? heading : f)) };
}

/**
 * The same definitions with every misplaced gate moved onto the block whose
 * heading asks for it. Returns the input array itself when nothing changed.
 */
export function liftNestedToggles(defs) {
  if (!Array.isArray(defs)) return defs;
  let changed = false;
  const out = defs.map(f => {
    if (!f || typeof f !== 'object') return f;
    let next = f;
    if (Array.isArray(f.fields)) {
      const kids = liftNestedToggles(f.fields);
      if (kids !== f.fields) next = { ...next, fields: kids };
    }
    if (f.conditions && typeof f.conditions === 'object') {
      let condChanged = false;
      const conditions = Object.fromEntries(Object.entries(f.conditions).map(([k, br]) => {
        const nb = liftNestedToggles(br);
        if (nb !== br) condChanged = true;
        return [k, nb];
      }));
      if (condChanged) next = { ...next, conditions };
    }
    next = liftToggleToQuestion(next);
    if (next !== f) changed = true;
    return next;
  });
  return changed ? out : defs;
}

/* ── Numbering around a hidden block ──────────────────────────────────────
   Hiding "(d) Details of each licensee" must not leave the entry reading
   (a) (b) (c) (e) (f) — an applicant reads the gap as a missing question. The
   siblings after a hidden block close up: (e) → (d), (f) → (e). Everywhere the
   entry is shown (form, saved-entry card, preview) runs the same function,
   so the number is the same on every screen.

   Only the gap is closed: each later sibling moves up by the number of hidden
   siblings of the SAME style before it, so a template's own numbering is never
   rewritten, and nothing changes at all while every block applies. */

/* A marker's position in its style: 'e' → 5, 'iv' → 4, '12' → 12. */
const markerOrdinal = (style, marker) => {
  const m = String(marker).toLowerCase();
  if (style === 'number') return parseInt(m, 10) || 0;
  if (style === 'lower-alpha' || style === 'upper-alpha') {
    return [...m].reduce((n, ch) => n * 26 + (ch.charCodeAt(0) - 96), 0);
  }
  if (style === 'lower-roman' || style === 'upper-roman') {
    const v = { i: 1, v: 5, x: 10, l: 50, c: 100, d: 500, m: 1000 };
    let n = 0;
    for (let i = 0; i < m.length; i += 1) {
      const cur = v[m[i]] || 0;
      n += cur < (v[m[i + 1]] || 0) ? -cur : cur;
    }
    return n;
  }
  return 0;
};

/**
 * The same sibling list with the numbering closed up around hidden blocks.
 * Same length and order as the input (hidden blocks stay in place — callers
 * already skip them), so positional keys and error keys do not move. Returns
 * the input itself when no block is hidden.
 *
 * @param {Array}    defs  one level of sibling definitions
 * @param {Function} get   field name → current value
 */
export function renumberAroundHidden(defs, get) {
  if (!Array.isArray(defs)) return defs;
  const isHidden = f => !!f && isBlockHidden(f, get);
  if (!defs.some(isHidden)) return defs;
  const passed = {};                        // style → hidden siblings passed so far
  return defs.map(f => {
    if (!f || typeof f !== 'object') return f;
    const key = f.sectionType === 'subSection' && f.sectionName ? 'sectionName' : 'label';
    const text = String(f[key] || '');
    const { marker } = splitLabelMarker(text);
    const style = markerStyle(marker);
    if (!style) return f;                   // not enumerated — nothing to close up
    if (isHidden(f)) { passed[style] = (passed[style] || 0) + 1; return f; }
    const shift = passed[style] || 0;
    // A repeatable's name is the key its rows are stored under — never rename it.
    if (!shift || f.type === 'repeatable') return f;
    const n = markerOrdinal(style, marker) - shift;
    if (n < 1) return f;
    // The marker is the label's first token, so its first occurrence is the one.
    return { ...f, [key]: text.replace(marker, formatMarker(style, n)) };
  });
}

export default isBlockHidden;
